// client/src/components/shared/AiAssistPanel.tsx (converted from AiAssistPanel.js, ST-00259)
//
// ST-00259: a failed create/update is reported once, in the modal (role="alert", scrolled into
// view), no longer also as a toast — toasts render since the app mounts AppToaster, so the same
// failure would otherwise show twice.
import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import type { AxiosResponse } from 'axios';
import {
  FaTimes,
  FaPaperPlane,
  FaSpinner,
  FaRobot,
  FaCheck,
  FaExclamationTriangle,
  FaFileCode,
  FaRedo,
  FaChevronDown,
  FaChevronUp,
  FaDatabase,
  FaMagic,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ThemedSelect from '../common/ThemedSelect';
import type { HttpErrorLike } from '../../types/clientShell';
import type {
  AiAssistModalProps,
  AiConversationTurn,
  AiCreatedQuery,
  AiCreatedReport,
  AiCreatedTemplate,
  AiGenerateResponse,
  AiGeneratedReport,
  AiTelegramChat,
} from '../../types/clientPages/aiAssist';

export type { AiAssistModalProps } from '../../types/clientPages/aiAssist';

type ScheduleFrequency = 'daily' | 'weekly' | 'monthly';

/** The API's reason for a failed request (`error`), else the error's message, else the fallback. */
const errorText = (err: unknown, fallback: string): string => {
  const e = err as HttpErrorLike | null | undefined;
  return e?.response?.data?.error || e?.message || fallback;
};

const statusOf = (err: unknown): number | undefined =>
  (err as HttpErrorLike | null | undefined)?.response?.status;

/** A created record from `{ data: row }` or a bare `row` (both answers exist). */
function unwrap<T>(res: AxiosResponse): T | null {
  const body = res.data as ({ data?: T } & T) | null | undefined;
  return body?.data || body || null;
}

const queryIdOf = (q: AiCreatedQuery | null) => q?.QueryID || q?.queryId || q?.id;
const templateIdOf = (t: AiCreatedTemplate | null) => t?.TemplateID || t?.templateId || t?.id;

/** scrollIntoView where the environment has it (jsdom does not). */
const reveal = (el: HTMLElement | null) => {
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
};

/**
 * AiAssistModal - Unified AI-powered modal that generates SQL query + template
 * and creates both via API in one flow. Supports editing existing reports
 * (`editReport`, Email Reports only). Props: AiAssistModalProps.
 */
const AiAssistModal = ({
  isOpen,
  onClose,
  outputType = 'email',
  onCreated,
  editReport,
}: AiAssistModalProps) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<AiGeneratedReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<AiConversationTurn[]>([]);
  const [showSQL, setShowSQL] = useState(true);
  const [showTemplate, setShowTemplate] = useState(true);
  const [queryName, setQueryName] = useState('');
  const [templateName, setTemplateName] = useState('');
  // Email report fields
  const [reportName, setReportName] = useState('');
  const [recipients, setRecipients] = useState<string[]>(['']);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleType, setScheduleType] = useState<ScheduleFrequency>('daily');
  const [scheduleTime, setScheduleTime] = useState('08:00');
  // Telegram schedule fields
  const [scheduleName, setScheduleName] = useState('');
  const [telegramChats, setTelegramChats] = useState<AiTelegramChat[]>([]);
  const [selectedChatId, setSelectedChatId] = useState('');
  const [loadingChats, setLoadingChats] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  // Set by a failed save: the error box is above the results and the footer's button, so it
  // is scrolled into view once it renders (ST-00259).
  const revealErrorRef = useRef(false);
  const isEditMode = !!editReport;

  const showSaveError = (message: string) => {
    revealErrorRef.current = true;
    setError(message);
  };

  // Fetch available Telegram chats when modal opens in telegram mode
  useEffect(() => {
    if (isOpen && outputType === 'telegram') {
      setLoadingChats(true);
      api.telegramBI.bot
        .getChats()
        .then((res) => {
          const body = res.data as { data?: unknown } | unknown[] | null | undefined;
          const chats = (body && !Array.isArray(body) ? body.data : undefined) || body || [];
          setTelegramChats(Array.isArray(chats) ? (chats as AiTelegramChat[]) : []);
        })
        .catch(() => setTelegramChats([]))
        .finally(() => setLoadingChats(false));
    }
  }, [isOpen, outputType]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
    if (!isOpen) {
      // Reset state when modal closes
      setPrompt('');
      setLoading(false);
      setCreating(false);
      setResult(null);
      setError(null);
      setConversationHistory([]);
      setQueryName('');
      setTemplateName('');
      setReportName('');
      setRecipients(['']);
      setScheduleEnabled(false);
      setScheduleType('daily');
      setScheduleTime('08:00');
      setScheduleName('');
      setSelectedChatId('');
      setTelegramChats([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && editReport) {
      setConversationHistory([
        {
          role: 'user',
          content: `Here is my existing report "${editReport.reportName}": ${editReport.description || ''}`,
        },
        {
          role: 'assistant',
          content: JSON.stringify({
            sql: editReport.querySql,
            template: editReport.templateHtml,
            subject: editReport.subjectTemplate || '',
          }),
        },
      ]);
      setQueryName(editReport.queryName || '');
      setTemplateName(editReport.templateName || '');
      setReportName(editReport.reportName || '');
    }
  }, [isOpen, editReport]);

  useEffect(() => {
    if (result) reveal(resultsRef.current);
  }, [result]);

  useEffect(() => {
    if (!error || !revealErrorRef.current) return;
    revealErrorRef.current = false;
    reveal(errorRef.current);
  }, [error]);

  const handleGenerate = async () => {
    if (!prompt.trim() || loading) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await api.ai.generate(prompt, outputType, conversationHistory);
      const data = (res.data as AiGenerateResponse | null | undefined)?.data;

      if (data?.sql && data?.template) {
        setResult({ ...data, sql: data.sql, template: data.template });
        // Auto-suggest names from the prompt
        const suggested = prompt.length > 50 ? prompt.substring(0, 50) + '...' : prompt;
        if (!queryName) {
          setQueryName(`AI: ${suggested}`);
        }
        if (!templateName) {
          setTemplateName(`AI: ${suggested}`);
        }
        if (!reportName && outputType === 'email') {
          setReportName(`AI: ${suggested}`);
        }
        if (!scheduleName && outputType === 'telegram') {
          setScheduleName(`AI: ${suggested}`);
        }
        setConversationHistory((prev) => [
          ...prev,
          { role: 'user', content: prompt },
          {
            role: 'assistant',
            content: JSON.stringify({ sql: data.sql, template: data.template }),
          },
        ]);
      } else {
        setError('Unexpected response format from AI');
      }
    } catch (err) {
      setError(errorText(err, 'Failed to generate'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleUpdateExisting = async () => {
    if (!result || !editReport) return;

    setCreating(true);
    setError(null);

    try {
      if (editReport.queryId) {
        await api.sharedQueries.update(editReport.queryId, {
          queryName: queryName.trim() || editReport.queryName,
          sqlQuery: result.sql,
        });
      }

      if (editReport.templateId) {
        await api.emailReports.updateTemplate(editReport.templateId, {
          templateName: templateName.trim() || editReport.templateName,
          subjectTemplate: result.subject || editReport.subjectTemplate,
          htmlTemplate: result.template,
        });
      }

      toast.success('Report updated successfully!');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      // Shown in the modal only (it stays open); a toast as well would say it twice.
      showSaveError(`Update failed: ${errorText(err, 'Failed to update')}`);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateBoth = async () => {
    if (isEditMode) {
      return handleUpdateExisting();
    }

    if (!result || !queryName.trim() || !templateName.trim()) {
      toast.error('Please provide names for both the query and template');
      return;
    }

    if (outputType === 'email') {
      if (!reportName.trim()) {
        toast.error('Please provide a report name');
        return;
      }
      const validRecipients = recipients.filter((r) => r.trim());
      if (validRecipients.length === 0) {
        toast.error('Please add at least one recipient email');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = validRecipients.filter((r) => !emailRegex.test(r.trim()));
      if (invalidEmails.length > 0) {
        toast.error(`Invalid email address: ${invalidEmails[0]}`);
        return;
      }
    }

    if (outputType === 'telegram') {
      if (scheduleEnabled) {
        if (!scheduleName.trim()) {
          toast.error('Please provide a schedule name');
          return;
        }
        if (!selectedChatId) {
          toast.error('Please select a Telegram chat to send to');
          return;
        }
      }
    }

    setCreating(true);
    setError(null);

    let createdQuery: AiCreatedQuery | null = null;
    let createdTemplate: AiCreatedTemplate | null = null;

    try {
      // Step 1: Create the query
      if (outputType === 'telegram') {
        const qRes = await api.telegramBI.queries.create({
          queryName: queryName.trim(),
          queryDescription: `AI-generated: ${prompt}`,
          sqlQuery: result.sql,
          dataSourceId: 1,
          isPublic: true,
          executionTimeoutSeconds: 300,
          maxRows: 10000,
        });
        createdQuery = unwrap<AiCreatedQuery>(qRes);
      } else {
        const qRes = await api.sharedQueries.create({
          queryName: queryName.trim(),
          description: `AI-generated: ${prompt}`,
          sqlQuery: result.sql,
          dataSourceId: 1,
          isPublic: true,
          executionTimeout: 300,
          maxRows: 10000,
        });
        createdQuery = unwrap<AiCreatedQuery>(qRes);
      }

      // Step 2: Create the template
      if (outputType === 'telegram') {
        const tRes = await api.telegramBI.templates.create({
          templateName: templateName.trim(),
          templateDescription: `AI-generated: ${prompt}`,
          templateContent: result.template,
          templateType: 'text',
          isPublic: true,
        });
        createdTemplate = unwrap<AiCreatedTemplate>(tRes);
      } else {
        const createEmailTemplate = async (name: string) => {
          return api.emailReports.createTemplate({
            templateName: name,
            description: `AI-generated: ${prompt}`,
            templateType: 'advanced_report',
            subjectTemplate: result.subject || `{{meta.reportName}} - {{meta.generatedAt}}`,
            htmlTemplate: result.template,
            isDefault: false,
          });
        };
        try {
          const tRes = await createEmailTemplate(templateName.trim());
          createdTemplate = unwrap<AiCreatedTemplate>(tRes);
        } catch (dupErr) {
          if (statusOf(dupErr) === 409) {
            const suffix = ` (${new Date().toLocaleDateString('en-ZA')})`;
            const tRes = await createEmailTemplate(templateName.trim() + suffix);
            createdTemplate = unwrap<AiCreatedTemplate>(tRes);
          } else {
            throw dupErr;
          }
        }
      }

      // Step 3 (Email only): Create the email report linking query + template
      let createdReport: AiCreatedReport | null | undefined;
      if (outputType === 'email') {
        const queryId = queryIdOf(createdQuery);
        const templateId = templateIdOf(createdTemplate);

        const createReport = async (name: string) => {
          return api.emailReports.create({
            reportName: name,
            description: `AI-generated: ${prompt}`,
            reportType: 'advanced',
            queryId: queryId,
            emailTemplateId: templateId,
            recipients: recipients.filter((r) => r.trim()),
            isActive: true,
          });
        };
        try {
          const reportRes = await createReport(reportName.trim());
          createdReport = unwrap<AiCreatedReport>(reportRes);
        } catch (dupErr) {
          if (statusOf(dupErr) === 409) {
            const suffix = ` (${new Date().toLocaleDateString('en-ZA')})`;
            const reportRes = await createReport(reportName.trim() + suffix);
            createdReport = unwrap<AiCreatedReport>(reportRes);
          } else {
            throw dupErr;
          }
        }

        // Step 4 (Email only): Create schedule if enabled
        if (scheduleEnabled && createdReport) {
          const reportId = createdReport.id || createdReport.ID || createdReport.ReportID;
          await api.emailReports.createSchedule(String(reportId), {
            scheduleType: scheduleType,
            scheduleTime: scheduleTime,
            timezone: 'Africa/Johannesburg',
            isActive: true,
          });
        }

        toast.success(
          'Email report created successfully! Query, template, and report are all set up.'
        );
      } else {
        // Telegram: create schedule if enabled
        let createdSchedule: AiCreatedReport | null | undefined;
        if (scheduleEnabled && selectedChatId) {
          const queryId = queryIdOf(createdQuery);
          const templateId = templateIdOf(createdTemplate);

          // Convert schedule type + time to cron expression
          const [hours, minutes] = scheduleTime.split(':').map(Number);
          let cronExpression: string | undefined;
          if (scheduleType === 'daily') {
            cronExpression = `${minutes} ${hours} * * *`;
          } else if (scheduleType === 'weekly') {
            cronExpression = `${minutes} ${hours} * * 1`; // Monday
          } else if (scheduleType === 'monthly') {
            cronExpression = `${minutes} ${hours} 1 * *`; // 1st of month
          }

          const schedRes = await api.telegramBI.schedules.create({
            scheduleName: scheduleName.trim(),
            queryId: queryId,
            templateId: templateId,
            chatRecordId: parseInt(selectedChatId, 10),
            scheduleType: 'cron',
            cronExpression,
            timezone: 'Africa/Johannesburg',
          });
          createdSchedule = unwrap<AiCreatedReport>(schedRes);

          toast.success('Telegram report created! Query, template, and schedule are all set up.');
        } else {
          toast.success('Query and template created successfully!');
        }

        createdReport = createdSchedule;
      }

      if (onCreated) {
        onCreated({ query: createdQuery, template: createdTemplate, report: createdReport });
      }

      onClose();
    } catch (err) {
      // Shown in the modal only (it stays open); a toast as well would say it twice.
      showSaveError(`Creation failed: ${errorText(err, 'Failed to create')}`);

      // Attempt cleanup of orphaned resources created before the failure
      try {
        if (createdQuery) {
          const qId = queryIdOf(createdQuery);
          if (qId) {
            if (outputType === 'telegram') {
              await api.telegramBI.queries.delete(qId).catch(() => {});
            } else {
              await api.sharedQueries.delete(qId).catch(() => {});
            }
          }
        }
        if (createdTemplate) {
          const tId = templateIdOf(createdTemplate);
          if (tId) {
            if (outputType === 'telegram') {
              await api.telegramBI.templates.delete(tId).catch(() => {});
            } else {
              await api.emailReports.deleteTemplate(tId).catch(() => {});
            }
          }
        }
      } catch {
        // Cleanup is best-effort
      }
    } finally {
      setCreating(false);
    }
  };

  const handleReset = () => {
    setPrompt('');
    setResult(null);
    setError(null);
    setConversationHistory([]);
    setQueryName('');
    setTemplateName('');
    setReportName('');
    setRecipients(['']);
    setScheduleEnabled(false);
    setScheduleType('daily');
    setScheduleTime('08:00');
    setScheduleName('');
    setSelectedChatId('');
  };

  if (!isOpen) return null;

  const examplePrompts = isEditMode
    ? [
        'Add a column showing average nett weight per ticket',
        'Change the sort order to show highest weights first',
        'Add a totals row at the bottom of the table',
      ]
    : outputType === 'telegram'
      ? [
          'Show me the top 10 dispatch locations by total weight this month',
          'Daily summary of ticket counts by ticket type for yesterday',
          'Compare weights between dispatch and receipt locations for the last 7 days',
        ]
      : [
          'Create a monthly summary report of total weights by dispatch location, sorted by weight descending',
          'Generate a report showing ticket counts and average weights by route for the current month',
          'Build a weekly report comparing dispatch vs receipt weights by location',
        ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-surface rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-surface/20 flex items-center justify-center backdrop-blur-sm">
              <FaRobot className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {isEditMode ? 'Edit with Lexi AI' : 'Lexi AI Assist'}
              </h2>
              <p className="text-xs text-indigo-200 dark:text-indigo-900">
                {isEditMode
                  ? `Modifying: ${editReport?.reportName}`
                  : outputType === 'telegram'
                    ? 'Generate a Telegram BI query + template from a description'
                    : 'Generate an email report query + template from a description'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface/10 text-white/70 hover:text-white transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Prompt Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {isEditMode
                ? 'Describe what you want to change'
                : 'Describe what you want the report to show'}
            </label>
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isEditMode
                    ? 'e.g., Add a column for average weight, change the sort order, add a totals row...'
                    : 'e.g., Show me a summary of total weights by dispatch location for this month, sorted by weight descending...'
                }
                rows={3}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all resize-none pr-12"
                disabled={loading || creating}
              />
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim() || loading || creating}
                className="absolute right-2 bottom-2 p-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Generate (Ctrl+Enter)"
              >
                {loading ? (
                  <FaSpinner className="text-sm animate-spin" />
                ) : (
                  <FaPaperPlane className="text-sm" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              Press Ctrl+Enter to generate
              {conversationHistory.length > 0
                ? ' | Send follow-up prompts to refine the results'
                : ''}
            </p>
          </div>

          {/* Example Prompts */}
          {!result && !loading && conversationHistory.length === 0 && (
            <div>
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-2">
                Try an example
              </p>
              <div className="space-y-1.5">
                {examplePrompts.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(ex)}
                    className="w-full text-left px-3 py-2 text-xs text-gray-600 bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-colors border border-transparent hover:border-indigo-200"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center gap-3 px-4 py-4 bg-indigo-50 rounded-lg border border-indigo-100">
              <FaSpinner className="text-indigo-600 animate-spin text-lg" />
              <div>
                <p className="text-sm font-medium text-indigo-700">
                  Generating your query and template...
                </p>
                <p className="text-xs text-indigo-500 mt-0.5">This usually takes 5-10 seconds</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              ref={errorRef}
              role="alert"
              className="flex items-start gap-2.5 px-4 py-3 bg-red-50 rounded-lg border border-red-200"
            >
              <FaExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700">Error</p>
                <p className="text-xs text-red-600 mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div ref={resultsRef} className="space-y-4">
              <div className="flex items-center gap-2">
                <FaMagic className="text-indigo-500 text-xs" />
                <span className="text-sm font-medium text-gray-800">Generated Results</span>
                <button
                  onClick={handleReset}
                  className="ml-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                  title="Start over"
                >
                  <FaRedo className="text-[10px]" />
                  Start over
                </button>
              </div>

              {/* SQL Preview */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowSQL(!showSQL)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FaDatabase className="text-emerald-500 text-sm" />
                    <span className="text-sm font-medium text-gray-700">SQL Query</span>
                  </div>
                  {showSQL ? (
                    <FaChevronUp className="text-gray-400 text-xs" />
                  ) : (
                    <FaChevronDown className="text-gray-400 text-xs" />
                  )}
                </button>
                {showSQL && (
                  <div className="bg-[#282c34] p-4 max-h-52 overflow-auto">
                    <pre className="text-xs font-mono text-[#abb2bf] whitespace-pre-wrap break-words">
                      {result.sql}
                    </pre>
                  </div>
                )}
              </div>

              {/* Template Preview */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowTemplate(!showTemplate)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FaFileCode className="text-violet-500 text-sm" />
                    <span className="text-sm font-medium text-gray-700">
                      {outputType === 'telegram' ? 'Telegram Template' : 'Email HTML Template'}
                    </span>
                  </div>
                  {showTemplate ? (
                    <FaChevronUp className="text-gray-400 text-xs" />
                  ) : (
                    <FaChevronDown className="text-gray-400 text-xs" />
                  )}
                </button>
                {showTemplate && (
                  <div className="bg-[#282c34] p-4 max-h-52 overflow-auto">
                    <pre className="text-xs font-mono text-[#abb2bf] whitespace-pre-wrap break-words">
                      {result.template}
                    </pre>
                  </div>
                )}
              </div>

              {/* Token usage */}
              {result.usage && (
                <p className="text-[10px] text-gray-400 text-right">
                  Tokens used: {result.usage.inputTokens} in / {result.usage.outputTokens} out
                </p>
              )}

              {/* Names for saving (hidden in edit mode — metadata managed via regular edit modal) */}
              {!isEditMode && (
                <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-medium text-indigo-700 uppercase tracking-wider">
                    {outputType === 'email' ? 'Report Details' : 'Save as'}
                  </p>

                  {/* Report name (email only) */}
                  {outputType === 'email' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Report Name *
                      </label>
                      <input
                        type="text"
                        value={reportName}
                        onChange={(e) => setReportName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                        placeholder="e.g., Daily Operations Summary"
                        disabled={creating}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Query Name *
                      </label>
                      <input
                        type="text"
                        value={queryName}
                        onChange={(e) => setQueryName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                        placeholder="e.g., Monthly Weight Summary"
                        disabled={creating}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Template Name *
                      </label>
                      <input
                        type="text"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                        placeholder="e.g., Monthly Weight Report"
                        disabled={creating}
                      />
                    </div>
                  </div>

                  {/* Recipients (email only) */}
                  {outputType === 'email' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Recipients *
                      </label>
                      {recipients.map((email, idx) => (
                        <div key={idx} className="flex items-center gap-2 mb-1.5">
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => {
                              const updated = [...recipients];
                              updated[idx] = e.target.value;
                              setRecipients(updated);
                            }}
                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                            placeholder="email@company.com"
                            disabled={creating}
                          />
                          {recipients.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setRecipients(recipients.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-600 text-xs p-1"
                              disabled={creating}
                            >
                              <FaTimes />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setRecipients([...recipients, ''])}
                        className="text-xs text-indigo-600 hover:text-indigo-700 mt-1"
                        disabled={creating}
                      >
                        + Add recipient
                      </button>
                    </div>
                  )}

                  {/* Schedule (email) */}
                  {outputType === 'email' && (
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={scheduleEnabled}
                          onChange={(e) => setScheduleEnabled(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          disabled={creating}
                        />
                        <span className="text-xs font-medium text-gray-600">Enable schedule</span>
                      </label>
                      {scheduleEnabled && (
                        <div className="flex items-center gap-3 mt-2 pl-6">
                          <ThemedSelect
                            value={scheduleType}
                            onChange={(e) =>
                              setScheduleType(String(e.target.value) as ScheduleFrequency)
                            }
                            className="text-sm"
                            disabled={creating}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </ThemedSelect>
                          <span className="text-xs text-gray-500">at</span>
                          <input
                            type="time"
                            value={scheduleTime}
                            onChange={(e) => setScheduleTime(e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                            disabled={creating}
                          />
                          <span className="text-xs text-gray-400">(SAST)</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Schedule & Chat (telegram) */}
                  {outputType === 'telegram' && (
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={scheduleEnabled}
                          onChange={(e) => setScheduleEnabled(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          disabled={creating}
                        />
                        <span className="text-xs font-medium text-gray-600">
                          Schedule delivery to Telegram
                        </span>
                      </label>
                      {scheduleEnabled && (
                        <div className="space-y-3 pl-6 border-l-2 border-indigo-200 ml-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Schedule Name *
                            </label>
                            <input
                              type="text"
                              value={scheduleName}
                              onChange={(e) => setScheduleName(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                              placeholder="e.g., Daily Weight Summary"
                              disabled={creating}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Send to Chat *
                            </label>
                            {loadingChats ? (
                              <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                                <FaSpinner className="animate-spin" /> Loading chats...
                              </div>
                            ) : telegramChats.length === 0 ? (
                              <p className="text-xs text-amber-600 py-2">
                                No Telegram chats registered. Send /start to your bot first, then
                                reopen this modal.
                              </p>
                            ) : (
                              <ThemedSelect
                                value={selectedChatId}
                                onChange={(e) => setSelectedChatId(String(e.target.value))}
                                className="w-full text-sm"
                                disabled={creating}
                                placeholder="Select a chat..."
                              >
                                <option value="">Select a chat...</option>
                                {telegramChats.map((chat) => (
                                  <option
                                    key={chat.ChatRecordID || chat.id}
                                    value={chat.ChatRecordID || chat.id}
                                  >
                                    {chat.ChatTitle ||
                                      chat.Username ||
                                      chat.FirstName ||
                                      `Chat ${chat.ChatID || chat.chatId}`}
                                    {chat.ChatType && chat.ChatType !== 'private'
                                      ? ` (${chat.ChatType})`
                                      : ''}
                                  </option>
                                ))}
                              </ThemedSelect>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <ThemedSelect
                              value={scheduleType}
                              onChange={(e) =>
                                setScheduleType(String(e.target.value) as ScheduleFrequency)
                              }
                              className="text-sm"
                              disabled={creating}
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </ThemedSelect>
                            <span className="text-xs text-gray-500">at</span>
                            <input
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => setScheduleTime(e.target.value)}
                              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                              disabled={creating}
                            />
                            <span className="text-xs text-gray-400">(SAST)</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {result && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
            <p className="text-xs text-gray-400">
              {isEditMode
                ? 'Updates the existing query and template in place'
                : outputType === 'email'
                  ? 'Creates query, template, and email report in one step'
                  : scheduleEnabled
                    ? 'Creates query, template, and scheduled Telegram delivery in one step'
                    : 'Creates query and template — you can schedule delivery later'}
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={creating}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBoth}
                disabled={
                  creating ||
                  (isEditMode
                    ? false
                    : !queryName.trim() ||
                      !templateName.trim() ||
                      (outputType === 'email' &&
                        (!reportName.trim() || recipients.filter((r) => r.trim()).length === 0)) ||
                      (outputType === 'telegram' &&
                        scheduleEnabled &&
                        (!scheduleName.trim() || !selectedChatId)))
                }
                className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating ? (
                  <>
                    <FaSpinner className="animate-spin text-xs" />{' '}
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <FaCheck className="text-xs" />{' '}
                    {isEditMode
                      ? 'Update Report'
                      : outputType === 'email'
                        ? 'Create Email Report'
                        : scheduleEnabled
                          ? 'Create Telegram Report'
                          : 'Create Query & Template'}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAssistModal;
